import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, MoreHorizontal, Clock, MessageSquare, Paperclip, Star, 
  Filter, Users, Calendar, ChevronDown, Zap, Flag, CheckCircle2,
  User, Search, Bell, Settings, X, Check
} from 'lucide-react';

const createInitialColumns = (companyName) => [
  {
    id: 'todo',
    title: 'To Do',
    color: '#64748b',
    tasks: [
      { id: 1, title: `${companyName} design system`, priority: 'high', comments: 5, attachments: 3, assignee: '👩‍💻', dueDate: 'Today', labels: ['Design', 'Docs'] },
      { id: 2, title: 'API rate limiting setup', priority: 'medium', comments: 2, attachments: 1, assignee: '👨‍💼', dueDate: 'Tomorrow', labels: ['Backend'] },
    ]
  },
  {
    id: 'progress',
    title: 'In Progress',
    color: '#6366f1',
    tasks: [
      { id: 3, title: 'User authentication flow', priority: 'high', comments: 8, attachments: 4, assignee: '👩‍🔬', dueDate: 'Dec 15', aiSuggestion: true, labels: ['Frontend', 'Auth'], progress: 65 },
      { id: 4, title: 'Dashboard analytics widgets', priority: 'medium', comments: 3, attachments: 2, assignee: '👨‍🎨', dueDate: 'Dec 18', labels: ['Frontend'], progress: 40 },
    ]
  },
  {
    id: 'review',
    title: 'In Review',
    color: '#f59e0b',
    tasks: [
      { id: 5, title: 'Payment integration - Stripe', priority: 'high', comments: 12, attachments: 6, assignee: '👩‍💻', dueDate: 'Dec 14', labels: ['Backend', 'Payments'], progress: 90 },
    ]
  },
  {
    id: 'done',
    title: 'Completed',
    color: '#10b981',
    tasks: [
      { id: 6, title: 'Onboarding flow redesign', priority: 'medium', comments: 7, attachments: 5, assignee: '👨‍🔬', labels: ['Design', 'UX'], completed: true },
    ]
  },
];

const priorityConfig = {
  high: { color: '#ef4444', bg: '#ef444420', label: 'High' },
  medium: { color: '#f59e0b', bg: '#f59e0b20', label: 'Medium' },
  low: { color: '#10b981', bg: '#10b98120', label: 'Low' }
};

const labelColors = {
  'Design': '#ec4899',
  'Frontend': '#6366f1',
  'Backend': '#10b981',
  'Docs': '#64748b',
  'Auth': '#f59e0b',
  'Payments': '#8b5cf6',
  'UX': '#06b6d4',
  'DB': '#84cc16'
};

export default function KanbanPreview({ customization }) {
  const theme = customization?.theme || { primary: '#6366f1', secondary: '#8b5cf6', gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' };
  const companyName = customization?.companyName || 'Your Company';
  
  const [columns, setColumns] = useState(() => createInitialColumns(companyName));
  const [draggedTask, setDraggedTask] = useState(null);
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const [showAddTask, setShowAddTask] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [dragOverTask, setDragOverTask] = useState(null);

  const handleDragStart = (e, task, columnId) => {
    setDraggedTask({ task, fromColumn: columnId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnId, taskId = null) => {
    e.preventDefault();
    setHoveredColumn(columnId);
    setDragOverTask(taskId);
  };

  const handleDrop = (toColumnId) => {
    if (!draggedTask) {
      setDraggedTask(null);
      setHoveredColumn(null);
      setDragOverTask(null);
      return;
    }

    if (draggedTask.fromColumn !== toColumnId) {
      setColumns(prev => prev.map(col => {
        if (col.id === draggedTask.fromColumn) {
          return { ...col, tasks: col.tasks.filter(t => t.id !== draggedTask.task.id) };
        }
        if (col.id === toColumnId) {
          const updatedTask = { 
            ...draggedTask.task, 
            completed: toColumnId === 'done',
            progress: toColumnId === 'done' ? 100 : toColumnId === 'review' ? 90 : toColumnId === 'progress' ? 50 : undefined
          };
          return { ...col, tasks: [...col.tasks, updatedTask] };
        }
        return col;
      }));
    }
    setDraggedTask(null);
    setHoveredColumn(null);
    setDragOverTask(null);
  };

  const handleAddTask = (columnId) => {
    if (!newTaskTitle.trim()) return;
    
    const newTask = {
      id: Date.now(),
      title: newTaskTitle,
      priority: 'medium',
      comments: 0,
      attachments: 0,
      assignee: '👤',
      dueDate: 'Next Week',
      labels: ['New']
    };
    
    setColumns(prev => prev.map(col => {
      if (col.id === columnId) {
        return { ...col, tasks: [...col.tasks, newTask] };
      }
      return col;
    }));
    
    setNewTaskTitle('');
    setShowAddTask(null);
  };

  const totalTasks = columns.reduce((sum, col) => sum + col.tasks.length, 0);
  const completedTasks = columns.find(c => c.id === 'done')?.tasks.length || 0;

  // Apply theme color to column headers dynamically
  const getColumnColor = (col) => {
    if (col.id === 'progress') return theme.primary;
    if (col.id === 'review') return theme.secondary || '#f59e0b';
    return col.color;
  };

  return (
    <div style={{ 
      height: '100%', 
      background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        background: 'white',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ 
            width: '32px', height: '32px', 
            background: theme.gradient,
            borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontWeight: 'bold', fontSize: '12px',
            boxShadow: `0 4px 12px ${theme.primary}40`
          }}>
            {companyName.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <h1 style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{companyName} Projects</h1>
              <span style={{ 
                fontSize: '8px', padding: '2px 6px', 
                background: `${theme.primary}20`, color: theme.primary, 
                borderRadius: '4px', fontWeight: '500' 
              }}>Sprint 4</span>
            </div>
            <p style={{ margin: 0, fontSize: '9px', color: '#64748b' }}>
              {completedTasks}/{totalTasks} tasks • Drag cards to move
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ display: 'flex', marginRight: '6px' }}>
            {['👩‍💻', '👨‍💼', '👩‍🔬', '👨‍🎨'].map((avatar, i) => (
              <div key={i} style={{
                width: '24px', height: '24px', borderRadius: '50%',
                background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', marginLeft: i > 0 ? '-8px' : 0,
                border: '2px solid white', zIndex: 4 - i
              }}>{avatar}</div>
            ))}
          </div>
          <button style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '6px 10px', background: '#f1f5f9', border: 'none',
            borderRadius: '6px', fontSize: '10px', color: '#475569', cursor: 'pointer'
          }}>
            <Filter size={12} /> Filter
          </button>
          <button style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '6px 10px', background: theme.gradient, border: 'none',
            borderRadius: '6px', fontSize: '10px', color: 'white', cursor: 'pointer',
            fontWeight: '500',
            boxShadow: `0 2px 8px ${theme.primary}40`
          }}>
            <Plus size={12} /> Add Task
          </button>
        </div>
      </div>

      {/* AI Suggestion Banner */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        style={{
          margin: '10px 14px',
          padding: '10px 12px',
          background: `linear-gradient(90deg, ${theme.primary}15, ${theme.primary}05)`,
          borderRadius: '10px',
          border: `1px solid ${theme.primary}30`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}
      >
        <div style={{
          width: '28px', height: '28px', borderRadius: '50%',
          background: theme.gradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Zap size={14} color="white" />
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: '10px', color: theme.primary, fontWeight: '500' }}>
            AI Priority Alert
          </p>
          <p style={{ margin: '2px 0 0', fontSize: '9px', color: `${theme.primary}cc` }}>
            "User authentication" is blocking 2 tasks. Move to top priority?
          </p>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '5px 10px', background: theme.primary, border: 'none',
              borderRadius: '5px', color: 'white', fontSize: '9px', cursor: 'pointer', fontWeight: '500'
            }}
          >Apply</motion.button>
          <button style={{
            padding: '5px 10px', background: 'transparent', border: `1px solid ${theme.primary}40`,
            borderRadius: '5px', color: theme.primary, fontSize: '9px', cursor: 'pointer'
          }}>Dismiss</button>
        </div>
      </motion.div>

      {/* Kanban Board */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        padding: '0 14px 14px',
        flex: 1,
        overflowX: 'auto'
      }}>
        {columns.map((column, colIdx) => (
          <motion.div
            key={column.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: colIdx * 0.1 }}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDragLeave={() => { setHoveredColumn(null); setDragOverTask(null); }}
            onDrop={() => handleDrop(column.id)}
            style={{
              minWidth: '200px',
              maxWidth: '200px',
              background: hoveredColumn === column.id ? `${theme.primary}08` : '#f8fafc',
              borderRadius: '12px',
              padding: '10px',
              display: 'flex',
              flexDirection: 'column',
              border: hoveredColumn === column.id ? `2px dashed ${theme.primary}` : '1px solid #e2e8f0',
              transition: 'all 0.2s'
            }}
          >
            {/* Column Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '10px',
              padding: '0 4px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ 
                  width: '10px', height: '10px', borderRadius: '3px', 
                  background: getColumnColor(column)
                }} />
                <span style={{ fontSize: '11px', fontWeight: '600', color: '#334155' }}>
                  {column.title}
                </span>
                <span style={{ 
                  fontSize: '9px', color: '#94a3b8',
                  background: '#e2e8f0', padding: '1px 6px', borderRadius: '10px'
                }}>
                  {column.tasks.length}
                </span>
              </div>
              <MoreHorizontal size={14} color="#94a3b8" style={{ cursor: 'pointer' }} />
            </div>

            {/* Tasks */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
              <AnimatePresence>
                {column.tasks.map((task, taskIdx) => (
                  <motion.div
                    key={task.id}
                    layout
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ 
                      opacity: draggedTask?.task.id === task.id ? 0.5 : 1, 
                      scale: 1,
                      y: dragOverTask === task.id ? 10 : 0
                    }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.15 }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task, column.id)}
                    onDragOver={(e) => handleDragOver(e, column.id, task.id)}
                    whileHover={{ scale: 1.02, boxShadow: `0 4px 12px rgba(0,0,0,0.1)` }}
                    style={{
                      background: 'white',
                      borderRadius: '10px',
                      padding: '10px',
                      cursor: 'grab',
                      border: task.aiSuggestion ? `1px solid ${theme.primary}50` : '1px solid #e2e8f0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}
                  >
                    {/* AI Badge */}
                    {task.aiSuggestion && (
                      <div style={{ 
                        display: 'flex', alignItems: 'center', gap: '4px',
                        marginBottom: '6px'
                      }}>
                        <Star size={10} fill={theme.primary} color={theme.primary} />
                        <span style={{ fontSize: '8px', color: theme.primary, fontWeight: '600' }}>
                          AI Priority
                        </span>
                      </div>
                    )}
                    
                    {/* Labels */}
                    {task.labels && (
                      <div style={{ display: 'flex', gap: '4px', marginBottom: '6px', flexWrap: 'wrap' }}>
                        {task.labels.map(label => (
                          <span key={label} style={{
                            fontSize: '8px', padding: '2px 6px',
                            background: label === 'New' ? `${theme.primary}20` : `${labelColors[label] || theme.primary}20`,
                            color: label === 'New' ? theme.primary : labelColors[label] || theme.primary,
                            borderRadius: '4px', fontWeight: '500'
                          }}>{label}</span>
                        ))}
                      </div>
                    )}
                    
                    {/* Title */}
                    <p style={{ 
                      margin: '0 0 8px', fontSize: '11px', fontWeight: '500', 
                      color: task.completed ? '#94a3b8' : '#1e293b',
                      textDecoration: task.completed ? 'line-through' : 'none',
                      lineHeight: 1.4
                    }}>
                      {task.title}
                    </p>
                    
                    {/* Progress bar */}
                    {task.progress && (
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                          <span style={{ fontSize: '8px', color: '#64748b' }}>Progress</span>
                          <span style={{ fontSize: '8px', color: theme.primary, fontWeight: '600' }}>{task.progress}%</span>
                        </div>
                        <div style={{ height: '4px', background: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
                          <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: `${task.progress}%` }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            style={{ height: '100%', background: theme.gradient, borderRadius: '2px' }}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Footer */}
                    <div style={{ 
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', color: '#94a3b8' }}>
                          <MessageSquare size={10} />
                          <span style={{ fontSize: '9px' }}>{task.comments}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', color: '#94a3b8' }}>
                          <Paperclip size={10} />
                          <span style={{ fontSize: '9px' }}>{task.attachments}</span>
                        </div>
                        {task.dueDate && (
                          <div style={{ 
                            display: 'flex', alignItems: 'center', gap: '2px', 
                            color: task.dueDate === 'Today' ? '#ef4444' : '#94a3b8'
                          }}>
                            <Clock size={10} />
                            <span style={{ fontSize: '9px' }}>{task.dueDate}</span>
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: '14px' }}>{task.assignee}</span>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Add Task Button/Form */}
            <AnimatePresence mode="wait">
              {showAddTask === column.id ? (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{ marginTop: '8px' }}
                >
                  <input
                    type="text"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    placeholder="Task title..."
                    autoFocus
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTask(column.id)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: `1px solid ${theme.primary}40`,
                      borderRadius: '6px',
                      fontSize: '11px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      marginBottom: '6px'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <motion.button 
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleAddTask(column.id)}
                      style={{
                        flex: 1, padding: '6px',
                        background: theme.gradient, border: 'none',
                        borderRadius: '6px', color: 'white', fontSize: '10px',
                        cursor: 'pointer', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: '4px'
                      }}
                    >
                      <Check size={12} /> Add
                    </motion.button>
                    <motion.button 
                      whileTap={{ scale: 0.95 }}
                      onClick={() => { setShowAddTask(null); setNewTaskTitle(''); }}
                      style={{
                        padding: '6px 10px',
                        background: '#f1f5f9', border: 'none',
                        borderRadius: '6px', color: '#64748b', fontSize: '10px',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={12} />
                    </motion.button>
                  </div>
                </motion.div>
              ) : (
                <motion.button 
                  key="button"
                  onClick={() => setShowAddTask(column.id)}
                  whileHover={{ background: '#e2e8f0' }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    width: '100%', marginTop: '8px', padding: '8px',
                    background: 'transparent', border: '1px dashed #cbd5e1',
                    borderRadius: '8px', color: '#94a3b8', fontSize: '10px',
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: '4px'
                  }}
                >
                  <Plus size={12} /> Add task
                </motion.button>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
